import { api } from './api';
import type { CommissionRule } from '@apptypes/index';

export interface CommissionConfig {
  mode: 'flat' | 'category';
  flatRate: number;
}

export interface CategoryRate {
  categoryId: string;
  categoryName: string;
  ruleId: string | null;
  rate: number | null; // null = uses the flat rate
}

export interface BusinessCategoryRate {
  categoryId: string;
  categoryName: string;
  platformRate: number | null; // platform default for this category
  businessRate: number | null;  // this business's override (null = inherits)
}

export interface BusinessCommission {
  mode: 'default' | 'flat' | 'category';
  flatRate: number | null;
  platformFlatRate: number;
  categoryRates: BusinessCategoryRate[];
}

export interface DeliveryTaxConfig {
  taxModel: 'trip_vatable' | 'commission_vatable' | 'no_vat';
  commissionRate: number;
  vatRate: number;
  commissionVatPayer: 'driver' | 'chekit';
}

export const commissionService = {
  getConfig: () => api.get<CommissionConfig>('/admin/commission/config'),

  // ── Driver payout tax model ──
  getDeliveryConfig: () => api.get<DeliveryTaxConfig>('/admin/commission/delivery-config'),
  setDeliveryConfig: (data: Partial<DeliveryTaxConfig>) =>
    api.put('/admin/commission/delivery-config', data),

  // ── Per-business (Chekit staff only) ──
  getBusinessCommission: (businessId: string) =>
    api.get<BusinessCommission>(`/admin/businesses/${businessId}/commission`),
  setBusinessCommission: (businessId: string, data: { mode?: string; flatRate?: number | null }) =>
    api.put(`/admin/businesses/${businessId}/commission`, data),
  setBusinessCategoryRate: (businessId: string, categoryId: string, rate: number) =>
    api.put(`/admin/businesses/${businessId}/commission/category-rates/${categoryId}`, { rate }),
  clearBusinessCategoryRate: (businessId: string, categoryId: string) =>
    api.delete(`/admin/businesses/${businessId}/commission/category-rates/${categoryId}`),

  setConfig: (data: { mode?: 'flat' | 'category'; flatRate?: number }) =>
    api.put('/admin/commission/config', data),

  listCategoryRates: () => api.get<CategoryRate[]>('/admin/commission/category-rates'),

  setCategoryRate: (categoryId: string, rate: number) =>
    api.put(`/admin/commission/category-rates/${categoryId}`, { rate }),

  clearCategoryRate: (categoryId: string) =>
    api.delete(`/admin/commission/category-rates/${categoryId}`),

  listRules: () =>
    api.get<CommissionRule[]>('/admin/commission-rules'),

  createRule: (data: Partial<CommissionRule>) =>
    api.post<CommissionRule>('/admin/commission-rules', data),

  updateRule: (ruleId: string, data: Partial<CommissionRule>) =>
    api.put<CommissionRule>(`/admin/commission-rules/${ruleId}`, data),

  deleteRule: (ruleId: string) =>
    api.delete(`/admin/commission-rules/${ruleId}`),

  getBusinessRates: (businessId: string) =>
    api.get<CommissionRule[]>(`/businesses/${businessId}/commission-rates`),
};
